import { NAV_MENU_TYPE } from '@eureka/shared/nav-bar';
import { E_ShareType } from '@eureka/services/src/types/share';
import { AuthService } from '@eureka/shared/share-tools/logic';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { RouterView, createRouter, createWebHashHistory } from 'vue-router';
import { proxyRouterList } from './proxy-router';
import { deprecatedRouter } from './deprecated-router';
import { HomeService } from '@/domains/home/services/home-service';
import { controlDeferred } from '@eureka/utils/control';
import { VITE_BASE_URL } from '@/constants/env';
import { isCNRegion } from '@eureka/utils/environment';
import { openPaymentSuccessDialog } from '@eureka/shared/payment-success-dialog';
import { touristRouterList } from './tourist-router';
import { syncLocationSearchToHashQuery } from '@eureka/utils/router';
import { AgentAuthService } from '@/domains/agent/services/agent-auth-service';

syncLocationSearchToHashQuery();

const router = createRouter({
    history: createWebHashHistory(VITE_BASE_URL),
    routes: [
        {
            path: '/',
            component: () => import('@/pages/Layout.vue'),
            redirect: '/home',
            children: [
                deprecatedRouter,
                {
                    path: '/home',
                    name: 'home',
                    component: () => import('@/pages/home/Home.vue'),
                },
                {
                    path: '/home/sar',
                    name: 'sar-home',
                    component: () => import('@/pages/home/SarHome.vue'),
                },
                {
                    path: '/enterprise',
                    name: 'enterprise',
                    redirect: '/home',
                },
                {
                    path: '/gallery',
                    name: 'gallery',
                    component: () => import('@/pages/gallery/Gallery.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.COMMUNITY,
                    },
                },
                {
                    path: '/explore',
                    name: 'explore',
                    component: () => import('@/pages/ai/Explore.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.CUSTOM_AGENT,
                    },
                },

                {
                    path: '/user-profile',
                    name: 'user-profile',
                    component: () => import('@/pages/UserProfile.vue'),
                },
                {
                    path: '/invitation',
                    name: 'invitation',
                    component: () => import('@/pages/invitation/Invitation.vue'),
                },
                {
                    name: 'ai-ls',
                    path: '/ai-ls',
                    children: [
                        {
                            path: 'lca/:agent_job_id',
                            name: 'ai-ls-lca-detail',
                            component: () => import('@/pages/ai-ls/LCAFlowResult.vue'),
                        },
                        {
                            path: 'sar/:agent_job_id',
                            name: 'ai-ls-sar-detail',
                            component: () => import('@/pages/ai-ls/sar/SarDetail.vue'),
                            props: (route) => ({ isSharePage: false }),
                            beforeEnter: async(to, from, next) => {
                                const isLogin = await isUserLogin();
                                if (!isLogin) {
                                    return next({ path: '/home/sar' });
                                }

                                const { isLsPlg } = await controlDeferred.promise;
                                if (!isCNRegion && !isLsPlg) {
                                    return next({ path: '/home' });
                                }

                                next();
                            },
                        },
                        {
                            path: 'sar-share/:agent_job_id',
                            name: 'ai-ls-sar-share-detail',
                            component: () => import('@/pages/ai-ls/sar/SarShareDetail.vue'),
                            props: (route) => ({ isSharePage: true }),
                        },
                        {
                            path: ':lsAgentName',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-entry',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lsAgentName/:lsAgentId',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-detail',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                            beforeEnter: async(to, from, next) => {
                                if (to.params.lsAgentName === 'document-agent') {
                                    const id = to.params.lsAgentId as string;
                                    const agentAuthService = injectService<AgentAuthService>(AgentAuthService);
                                    const hasPermission = await agentAuthService.checkDocumentAnalyzerPermission(id);
                                    if (hasPermission) {
                                        next();
                                    } else {
                                        next({ name: 'home', query: { agent: 'pda' } });
                                    }
                                } else {
                                    next()
                                }
                            },
                        },
                        {
                            path: ':lifesciences*',
                            name: 'ai-ls-page',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        }
                    ],
                },
                {
                    name: 'chat',
                    path: '/chat/:id',
                    component: () => import('@/pages/ls/chat.vue'),
                },
                {
                    name: 'chat-share',
                    path: '/chat/share/:id',
                    component: () => import('@/pages/ls/chat-share.vue'),
                },
                {
                    name: 'userSetting',
                    path: '/user-setting',
                    component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Layout.vue'),
                    children: [
                        {
                            path: 'amount',
                            name: 'userSettingAmount',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Amount.vue'),
                        },
                        {
                            path: 'package',
                            name: 'userSettingPackage',
                            component: RouterView,
                            children: [
                                {
                                    path: 'ls',
                                    name: 'userSettingPackageLS',
                                    component: () => import(/* webpackChunkName: "air-usersetting-LS" */'@/pages/user-setting/new-version/product-lines/LS.vue'),
                                },
                                {
                                    path: 'ls/plg',
                                    name: 'userSettingPackageLS_PLG',
                                    component: () => import(/* webpackChunkName: "air-usersetting-LSPlg" */'@/pages/user-setting/new-version/product-lines/LsPlg.vue'),
                                    beforeEnter: async(to, from, next) => {
                                        const { isLsPlg } = await controlDeferred.promise;
                                        if (isLsPlg) {
                                            next();
                                        } else {
                                            next({ path: '/home' });
                                        }
                                    },
                                },
                                {
                                    path: 'ls/plg/payment/success',
                                    name: 'userSettingPackageLS_PLG_PaymentSuccess',
                                    component: () => import(/* webpackChunkName: "air-usersetting-LSPlgPaymentSuccess" */'@/pages/user-setting/new-version/product-lines/LsPlgPaymentSuccess.vue'),
                                }
                            ],
                        },
                        {
                            path: 'agent-settings',
                            name: 'userSettingAgentSettings',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/AgentSettings.vue'),
                        }
                    ],
                },
                {
                    name: 'graph',
                    path: '/graph',
                    meta: {
                        navModuleType: NAV_MENU_TYPE.WORK_PROJECT,
                    },
                    children: [
                        {
                            path: '',
                            name: 'GraphHome',
                            component: () => import('@/pages/graph/index.vue'),
                        },
                        {
                            path: ':graph_id',
                            name: 'GraphDetail',
                            component: () => import('@/pages/graph/detail.vue'),
                            beforeEnter: async(to, from, next) => {
                                const authService = injectService<AuthService>(AuthService);
                                await authService.shareAccessCheck({
                                    data_id: to.params.graph_id as string,
                                    data_type: E_ShareType.PROJECT,
                                });
                                next();
                            },
                        },
                        {
                            path: ':graph_id/report',
                            name: 'GraphReport',
                            component: () => import('@/pages/graph/report.vue'),
                        },
                        {
                            path: ':graph_id/pdf/:item_id',
                            name: 'GraphPdf',
                            component: () => import('@/pages/graph/pdf.vue'),
                        }
                    ],
                },
                {
                    path: '/403',
                    component: () => import('@/pages/403/Unauthorized.vue'),
                },
                {
                    path: '/404',
                    component: () => import('@/pages/404/NotFound.vue'),
                }
            ],
        },

        ...touristRouterList,
        {
            name: 'onboarding',
            path: '/onboarding',
            component: () => import('@/pages/Onboarding.vue'),
        },
        {
            name: 'subscribe',
            path: '/subscribe',
            component: () => import('@/pages/subscribe/Subscribe.vue'),
        },
        {
            path: '/ai',
            name: 'ai',
            redirect: '/home',
            component: () => import('@/pages/Layout.vue'),
            meta: {
                navModuleType: NAV_MENU_TYPE.AI_GENERATE,
            },
            children: [
                {
                    path: '/ai/agent',
                    name: 'ai-agent',
                    redirect: '/home',
                },
                {
                    path: '/ai/agent-flow/:agent_job_id',
                    name: 'ai-agent-flow',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.agent_job_id as string,
                            data_type: E_ShareType.AI_AGENT,
                        });
                        next();
                    },
                    component: () => import('@/pages/ai/AgentFlow.vue'),
                }
            ],
        },
        ...proxyRouterList,
        // 通配符路由 - 捕获所有未匹配的路径并重定向到404页面
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            redirect: '/404',
        }
    ],
});

router.beforeEach(async(to, _from, next) => {
    // 全局路由守卫（如需添加全局逻辑，可在此处添加）
    // const isGuest = !await isUserLogin();
    const isCN = isCNRegion();
    switch (to.name) {
    case 'userSettingPackage': {
        // 只有当访问 userSettingPackage 本身时，才进行重定向
        let homepageType = isCN ? 'LS' : 'LS_PLG';
        try {
            const homeService = injectService<HomeService>(HomeService);
            const data = await homeService.getHomePageType();
            homepageType = data.homepage_type;
        } catch {
        }

        switch (homepageType) {
        case 'RD':
        case 'IP':
        case 'MSE':
        case 'LS': {
            return next({
                name: 'userSettingPackageLS',
                query: to.query,
            })
        }
        case 'LS_PLG':
            return next({
                name: isCN ? 'userSettingPackageLS' : 'userSettingPackageLS_PLG',
                query: to.query,
            })
        default:
            break;
        }
        break;
    }
    default:{
        break;
    }
    }
    next();

    // 如果localhost里面有show_payment_success_dialog参数，则弹出支付成功弹框
    const account = await accountDeferred.promise;
    if (localStorage.getItem('show_payment_success_dialog')) {
        openPaymentSuccessDialog({ email: account.email })
        localStorage.removeItem('show_payment_success_dialog');
    }
});

export default router;
